import { useState, useEffect, useCallback } from "react";
import { GridFilterModel } from "@mui/x-data-grid-premium";

export interface DataGridSettings {
  alternatingRows: boolean;
  oddRowColor: string;
  evenRowColor: string;
  fontSize: number;
  cellPadding: number;
  stackColumnHeaders: boolean;
  actionsColumnPosition: "front" | "back";
  preserveSelectionOnSearch: boolean;
}

export const DEFAULT_SETTINGS: DataGridSettings = {
  alternatingRows: true,
  oddRowColor: "#e8edf5",
  evenRowColor: "#ffffff",
  fontSize: 13,
  cellPadding: 16,
  stackColumnHeaders: false,
  actionsColumnPosition: "back",
  preserveSelectionOnSearch: false,
};

const STORAGE_KEY = "datagrid-global-settings";
const CHANGE_EVENT = "datagrid-settings-changed";

export function loadSettings(): DataGridSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
    }
  } catch (e) {
    console.log(e);
  }
  return { ...DEFAULT_SETTINGS };
}

export function saveSettings(settings: DataGridSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  } catch (e) {
    console.log(e);
  }
}

export function useDataGridSettings() {
  const [settings, setSettings] = useState<DataGridSettings>(loadSettings);

  useEffect(() => {
    const handler = () => setSettings(loadSettings());
    window.addEventListener(CHANGE_EVENT, handler);
    return () => window.removeEventListener(CHANGE_EVENT, handler);
  }, []);

  const updateSettings = useCallback((newSettings: DataGridSettings) => {
    setSettings(newSettings);
    saveSettings(newSettings);
  }, []);

  return { settings, updateSettings };
}

function defaultFilterKey(tableName: string) {
  return `sdg-default-filters:${tableName}`;
}

export function loadDefaultFilterModel(
  tableName: string
): GridFilterModel | null {
  try {
    const stored = localStorage.getItem(defaultFilterKey(tableName));
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

export function saveDefaultFilterModel(
  tableName: string,
  model: GridFilterModel
): void {
  try {
    localStorage.setItem(defaultFilterKey(tableName), JSON.stringify(model));
  } catch (e) {
    console.log(e);
  }
}

export function clearDefaultFilterModel(tableName: string): void {
  localStorage.removeItem(defaultFilterKey(tableName));
}
